import React, { memo, useCallback, useEffect, useId, useRef } from 'react';
import { InputAdornment } from '@mui/material';
import { TextField as MuiTextField } from 'formik-mui';
import useFocusOnError from './use-focus-on-error';
import { perToEnDigits, toNumber } from 'utils/utils';
import { getIn } from 'formik';
import { useFloatingLabel } from 'components/inputs/use-floating-label';

export const TextField: React.FC<any> = memo(
    ({
        icon,
        secondIcon,
        description,
        showValidation = true,
        liveValidation = true,
        numericOnly = false,
        formatThousands = false,
        disableAutoFill = false,
        clearErrorOnChange = false,
        inputSanitizer,
        ...rest
    }) => {
        const { field, form } = rest;
        const { name, value } = field;
        const {
            status,
            errors,
            touched,
            submitCount,
            validateOnBlur,
            validateOnChange,
            setStatus,
            setFieldError,
            handleChange,
            validateField,
        } = form;
        const textRef = useRef();
        const generatedId = useId().replace(/:/g, '');

        const fieldTouched = getIn(touched, name);
        const fieldError = getIn(errors, name);
        const fieldStatus = getIn(status, name);
        const deferValidationUntilSubmit =
            validateOnBlur === false && validateOnChange === false;
        const canShowValidation =
            showValidation &&
            (Boolean(fieldTouched) ||
                Boolean(fieldStatus) ||
                (deferValidationUntilSubmit && submitCount > 0));

        const handleFieldChange = useCallback(
            (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
                let nextValue = event.target.value;

                if (inputSanitizer) {
                    nextValue = inputSanitizer(nextValue);
                }

                if (/[۰-۹]/.test(nextValue)) {
                    nextValue = perToEnDigits(nextValue);
                }

                if (numericOnly || formatThousands) {
                    nextValue = nextValue.replace(/\D/g, '');
                }

                event.target.value = nextValue;
                handleChange(event);

                if (clearErrorOnChange && fieldError) {
                    setFieldError(name, undefined);
                }

                if (fieldStatus) {
                    const { [name]: _, ...newStatus } = status;
                    setStatus(newStatus);
                }
            },
            [
                status,
                name,
                value,
                numericOnly,
                formatThousands,
                inputSanitizer,
                clearErrorOnChange,
                fieldError,
                fieldStatus,
                handleChange,
                setFieldError,
                setStatus,
            ],
        );

        const timeoutRef = useRef<NodeJS.Timeout | null>(null);

        useEffect(() => {
            if (!liveValidation) return;
            if (deferValidationUntilSubmit && submitCount === 0) return;

            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => validateField(name), 500);
            return () => clearTimeout(timeoutRef.current!);
        }, [
            liveValidation,
            name,
            validateField,
            value,
            deferValidationUntilSubmit,
            submitCount,
        ]);

        useFocusOnError({
            fieldRef: textRef,
            name: field ? name : null,
        });

        const handleAutoFillFocus: any = useCallback(
            (event: any) => {
                if (disableAutoFill) {
                    event.target.autocomplete = 'new-password';
                } else if (event.target.autocomplete) {
                    event.target.autocomplete = 'whatever';
                }

                rest?.onFocus?.(event);
            },
            [disableAutoFill, rest?.onFocus],
        );

        const floatingLabel = useFloatingLabel({
            value,
            onFocus: handleAutoFillFocus,
            onBlur: field?.onBlur,
        });

        return (
            <>
                <MuiTextField
                    {...rest}
                    inputRef={textRef}
                    value={
                        formatThousands
                            ? String(value ?? '')
                                  .replace(/\D/g, '')
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            : rest?.customtype === 'number'
                              ? toNumber(value)
                              : value || ''
                    }
                    fullWidth
                    className={rest?.ltr && 'ltr-text'}
                    onFocus={floatingLabel.onFocus}
                    onBlur={floatingLabel.onBlur}
                    autoComplete={disableAutoFill ? 'new-password' : 'off'}
                    id={
                        disableAutoFill
                            ? `address-field-${generatedId}`
                            : rest?.id
                    }
                    onChange={handleFieldChange(name)}
                    error={
                        canShowValidation &&
                        Boolean(fieldError || Boolean(fieldStatus))
                    }
                    helperText={
                        canShowValidation && (fieldError || fieldStatus)
                            ? fieldError || fieldStatus
                            : description
                    }
                    InputLabelProps={{
                        ...rest?.InputLabelProps,
                        shrink:
                            rest?.InputLabelProps?.shrink ??
                            floatingLabel.shrink,
                    }}
                    InputProps={{
                        startAdornment: icon ? (
                            <InputAdornment position="start">
                                {icon}
                            </InputAdornment>
                        ) : undefined,
                        endAdornment: secondIcon ? (
                            <InputAdornment position="end">
                                {secondIcon}
                            </InputAdornment>
                        ) : undefined,
                        ...rest?.InputProps,
                        autoComplete: disableAutoFill
                            ? 'new-password'
                            : 'off',
                    }}
                    inputProps={{
                        ...rest?.inputProps,
                        autoComplete: disableAutoFill
                            ? 'new-password'
                            : rest?.inputProps?.autoComplete,
                        ...(disableAutoFill
                            ? {
                                  'data-lpignore': 'true',
                                  'data-1p-ignore': 'true',
                                  'data-form-type': 'other',
                                  autoCapitalize: 'off',
                                  autoCorrect: 'off',
                                  spellCheck: false,
                              }
                            : {}),
                        maxLength:
                            rest?.maxLength ??
                            rest?.inputProps?.maxLength ??
                            undefined,
                    }}
                />
            </>
        );
    },
);
